import React, { useEffect, useMemo, useState } from "react";
import type { TimetableSession } from "../../lib/api";
import {
  buildMonthGrid,
  dayHeadingInTz,
  dayKeyInTz,
  isInMonth,
  resolveCategory,
  timeInTz,
  type YearMonth,
} from "./lib";
import { SessionRow } from "./ListView";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MAX_CHIPS = 3;
const MOBILE_QUERY = "(max-width: 719px)";

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

type Props = {
  ym: YearMonth;
  days: Map<string, TimetableSession[]>;
  tz: string;
  locationsById: Map<string, string>;
  onSessionClick: (s: TimetableSession) => void;
  onMoreClick: (dayKey: string) => void;
  onBook: (s: TimetableSession) => void;
};

export function CalendarView({ ym, days, tz, locationsById, onSessionClick, onMoreClick, onBook }: Props) {
  const isMobile = useIsMobile();
  const [selectedDayKey, setSelectedDayKey] = useState<string | undefined>();
  // Keyed on `ym` itself (not [ym.year, ym.month]) — ym is only ever replaced
  // with a new object when the month actually changes (see changeMonth in
  // WhatsOn.tsx), so this is equivalent, and matches what the React Compiler
  // can safely preserve as manual memoization.
  const cells = useMemo(() => buildMonthGrid(ym).flat(), [ym]);
  const todayKey = useMemo(() => dayKeyInTz(new Date().toISOString(), tz), [tz]);

  // reset day selection when the month changes
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setSelectedDayKey(undefined), [ym.year, ym.month]);

  const effectiveSelectedDayKey =
    selectedDayKey && isInMonth(selectedDayKey, ym) ? selectedDayKey : undefined;

  const selectedSessions = effectiveSelectedDayKey ? (days.get(effectiveSelectedDayKey) ?? []) : [];

  return (
    <div>
      <div className="bwo-grid" role="grid">
        {DOW.map((d) => (
          <div key={d} className="bwo-dow">{d}</div>
        ))}
        {cells.map((cell) => {
          const sessions = cell.inMonth ? (days.get(cell.dayKey) ?? []) : [];
          const classes = [
            "bwo-cell",
            cell.inMonth && "bwo-cell--inmonth",
            cell.dayKey === todayKey && "bwo-cell--today",
            cell.dayKey === effectiveSelectedDayKey && "bwo-cell--selected",
          ].filter(Boolean).join(" ");
          const isInteractive = isMobile && sessions.length;
          const handleToggle = () => setSelectedDayKey(
            effectiveSelectedDayKey === cell.dayKey ? undefined : cell.dayKey,
          );
          const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
              handleToggle();
            } else if (e.key === " ") {
              e.preventDefault();
              handleToggle();
            }
          };

          return (
            <div
              key={cell.dayKey}
              className={classes}
              onClick={isInteractive ? handleToggle : undefined}
              role={isInteractive ? "button" : undefined}
              tabIndex={isInteractive ? 0 : undefined}
              aria-expanded={isInteractive ? (cell.dayKey === effectiveSelectedDayKey) : undefined}
              onKeyDown={isInteractive ? handleKeyDown : undefined}
            >
              <span className="bwo-cell-day">{cell.day}</span>
              {sessions.slice(0, MAX_CHIPS).map((s) => {
                const cat = resolveCategory(s.course);
                const soldOut = s.availability <= 0;
                return (
                  <button
                    key={s._id}
                    type="button"
                    className={`bwo-chip${soldOut ? " bwo-chip--soldout" : ""}`}
                    style={{ background: cat.bg, color: cat.fg }}
                    onClick={() => onSessionClick(s)}
                    title={s.title || s.course?.title}
                  >
                    {timeInTz(s.startDateTime, tz)} {s.title || s.course?.title}
                    {soldOut ? " · Sold out" : ""}
                  </button>
                );
              })}
              {sessions.length > MAX_CHIPS && (
                <button type="button" className="bwo-more" onClick={() => onMoreClick(cell.dayKey)}>
                  +{sessions.length - MAX_CHIPS} more
                </button>
              )}
              <div className="bwo-dots">
                {sessions.slice(0, MAX_CHIPS).map((s) => (
                  <span key={s._id} className="bwo-dot" style={{ background: resolveCategory(s.course).dot }} />
                ))}
                {sessions.length > MAX_CHIPS && <span className="bwo-cell-day">+{sessions.length - MAX_CHIPS}</span>}
              </div>
            </div>
          );
        })}
      </div>
      {isMobile && selectedSessions.length > 0 && (
        <div className="bwo-daypanel">
          <h4 className="bwo-day-heading">
            {dayHeadingInTz(selectedSessions[0].startDateTime, tz)}
          </h4>
          {selectedSessions.map((s) => (
            <SessionRow
              key={s._id}
              session={s}
              tz={tz}
              locationName={s.locationId ? locationsById.get(s.locationId) : undefined}
              highlighted={false}
              showDate={false}
              onBook={onBook}
            />
          ))}
        </div>
      )}
    </div>
  );
}
